import { useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useSearchParams } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import type { PaymentStatus } from '@/lib/api-admin/types';
import { Button, PageShell, Pagination, Skeleton } from '@/components/ui';
import { usePaymentFiltersStore } from '@/stores/usePaymentFiltersStore';
import { PAYMENT_STATUS_OPTIONS, PaymentFiltersBar } from '../components/PaymentFiltersBar';
import { PaymentsTable } from '../components/PaymentsTable';
import { usePaymentsQuery } from '../hooks/usePaymentsQuery';

export const PaymentListPage = () => {
  const { t } = useTranslation('payments');

  const keyword = usePaymentFiltersStore((s) => s.keyword);
  const paymentStatus = usePaymentFiltersStore((s) => s.paymentStatus);
  const paymentMethod = usePaymentFiltersStore((s) => s.paymentMethod);
  const provider = usePaymentFiltersStore((s) => s.provider);
  const fromDate = usePaymentFiltersStore((s) => s.fromDate);
  const toDate = usePaymentFiltersStore((s) => s.toDate);
  const minAmount = usePaymentFiltersStore((s) => s.minAmount);
  const maxAmount = usePaymentFiltersStore((s) => s.maxAmount);
  const hasRefund = usePaymentFiltersStore((s) => s.hasRefund);
  const excludeLegacy = usePaymentFiltersStore((s) => s.excludeLegacy);
  const page = usePaymentFiltersStore((s) => s.page);
  const size = usePaymentFiltersStore((s) => s.size);
  const sort = usePaymentFiltersStore((s) => s.sort);
  const setPage = usePaymentFiltersStore((s) => s.setPage);
  const setSort = usePaymentFiltersStore((s) => s.setSort);

  // 대시보드 카드 등에서 ?paymentStatus=…&excludeLegacy=true 로 들어오면 그 조건으로 연다. 카드 건수와 같게
  // 다른 필터는 비우고 적용한 뒤 URL 에서 지운다 — 이후에는 필터 바(스토어)가 기준이라, 체크를 풀면 이관 포함으로 조회된다.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const status = searchParams.get('paymentStatus');
    const legacy = searchParams.get('excludeLegacy');
    if (status === null && legacy === null) return;
    const store = usePaymentFiltersStore.getState();
    store.reset();
    if (status && (PAYMENT_STATUS_OPTIONS as ReadonlyArray<string>).includes(status)) {
      store.setPaymentStatus(status as PaymentStatus);
    }
    if (legacy === 'true') store.setExcludeLegacy(true);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const query = usePaymentsQuery({
    page,
    size,
    sort,
    keyword,
    paymentStatus,
    paymentMethod,
    provider,
    fromDate,
    toDate,
    minAmount,
    maxAmount,
    hasRefund,
    excludeLegacy,
  });

  const items = query.data?.items ?? [];
  const totalItems = query.data?.meta.pagination?.totalItems ?? 0;

  return (
    <PageShell title={t('payments.list.title')}>
      <PaymentFiltersBar />

      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2" data-testid="payments-loading">
          <p className="flex items-center gap-2 text-sm" style={{ color: 'var(--content-muted)' }}>
            <Loader2 size={14} className="animate-spin" aria-hidden="true" />
            {t('payments.list.loading')}
          </p>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[var(--density-default-row-h)] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>{t('payments.list.error')}</p>
          {query.error?.message ? (
            <p className="max-w-md text-xs" style={{ color: 'var(--content-muted)' }}>
              {query.error.message}
            </p>
          ) : null}
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('payments.list.retry')}
          </Button>
        </div>
      ) : (
        // 필터·검색·페이지를 바꾸면 이전 결과를 흐리게 두고 위에 "검색 중" 을 띄운다(BE 검색이 느릴 때 멈춘 것처럼 보이지 않게).
        <div className="relative" aria-busy={query.isFetching}>
          {query.isFetching ? (
            <div
              role="status"
              aria-live="polite"
              className="absolute inset-x-0 top-0 z-10 flex items-center justify-center gap-2 rounded-md py-2 text-sm font-medium shadow-sm"
              style={{ background: 'var(--surface-raised)', color: 'var(--content-strong)' }}
              data-testid="payments-fetching"
            >
              <Loader2 size={16} className="animate-spin" aria-hidden="true" />
              {t('payments.list.searching', '검색 중입니다… 결제 건이 많으면 조금 걸릴 수 있어요.')}
            </div>
          ) : null}
          <div className={query.isFetching ? 'pointer-events-none opacity-50 transition-opacity' : 'transition-opacity'}>
            <PaymentsTable items={items} sort={sort} onSortChange={setSort} />
          </div>
        </div>
      )}

      <Pagination
        total={totalItems ?? 0}
        currentPage={page}
        pageSize={size}
        onPageChange={setPage}
        className="mt-4"
      />
    </PageShell>
  );
};
